import { useEffect, useState } from 'react'
import { Stepper, Step, Box, useResponsiveValue } from '@machobear/design-system'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router'
import { isNullOrEmpty } from '@/utils'
import { FORM_STEPS } from '../../util/form-steps'
import { FormStep, Listing } from '../../types'
import { detailsValidationSchema, mediaValidationSchema } from '../../util/vallidationSchema'

const validateDetails = async (data: Listing) => {
  return await detailsValidationSchema.isValid(data)
}

const validateMedia = async (data: Listing) => {
  return await mediaValidationSchema.isValid(data)
}

interface FormStepperProps {
  step: FormStep
  data: Listing
}

const FormStepper = ({ step, data }: FormStepperProps) => {
  const { t } = useTranslation()
  const history = useHistory()
  const { listingId } = useParams<{ listingId: string }>()
  const isTablet = useResponsiveValue()({
    mobile: false,
    tablet: true,
    desktop: false
  })

  const activeStep = FORM_STEPS[step]
  const activeStepNumber = activeStep.stepNumber

  const createStepComplete = !isNullOrEmpty(listingId)
  const [detailsStepComplete, setDetailsStepComplete] = useState(
    activeStepNumber > FORM_STEPS['listing-details'].stepNumber
  )
  const [mediaStepComplete, setMediaStepComplete] = useState(
    activeStepNumber > FORM_STEPS['listing-media'].stepNumber
  )

  useEffect(() => {
    const validateSteps = async () => {
      const detailsValid = await validateDetails(data)
      const mediaValid = await validateMedia(data)
      setDetailsStepComplete(detailsValid)
      setMediaStepComplete(mediaValid)
    }
    if (!isNullOrEmpty(data)) {
      validateSteps()
    }
  }, [data])

  const getCurrentStep = () => {
    if (!createStepComplete) return FORM_STEPS['create-listing'].stepNumber
    if (!detailsStepComplete) return FORM_STEPS['listing-details'].stepNumber
    if (!mediaStepComplete) return FORM_STEPS['listing-media'].stepNumber
    return FORM_STEPS['listing-summary'].stepNumber
  }
  const currentStep = getCurrentStep()

  const handleStepClick = (id: FormStep) => {
    const clickedStep = FORM_STEPS[id]
    history.push(clickedStep.path.replace(':listingId', listingId))
  }

  return (
    <Box
      paddingRight={{ mobile: 'gutter', tablet: 'large' }}
      paddingLeft={{ mobile: 'gutter', tablet: 'none' }}
      paddingBottom={{ mobile: 'gutter', tablet: 'xlarge', desktop: 'none' }}
      width="full"
    >
      <Stepper
        label={t('Create a new listing', 'Create a new listing')}
        mode="linear"
        progress={currentStep}
        activeStep={activeStepNumber}
        direction={isTablet ? 'horizontal' : 'vertical'}
        showProgress={false}
        stepCountLabel={t('of', 'of')}
        nextLabel={`${t('Next', 'Next')}:`}
        onStepClick={({ id }) => handleStepClick(id as FormStep)}
      >
        <Step
          id="create-listing"
          label={t('Create Listing', 'Create Listing')}
          complete={createStepComplete}
        />
        <Step
          id="listing-details"
          label={t('Listing Details', 'Listing Details')}
          complete={detailsStepComplete}
        />
        <Step id="listing-media" label={t('Media', 'Media')} complete={mediaStepComplete} />
        <Step id="listing-summary" label={t('Listing Summary', 'Listing Summary')} />
      </Stepper>
    </Box>
  )
}

export default FormStepper
